<template>
  <div class="page-content">
    <breadcrumb-index :items="breadcrumbs">
      <template #actions>
        <router-link to="/app/exams/list" class="btn btn-outline-secondary btn-sm">
          <vue-feather type="arrow-left" size="14" class="me-1" />
          Back
        </router-link>
      </template>
    </breadcrumb-index>

    <!-- Loading State -->
    <div v-if="pageLoading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <p class="text-muted mt-2">Loading exam...</p>
    </div>

    <template v-else>
      <div class="row g-3 mb-4">
        <div class="col-md-4">
          <router-link :to="`/app/exams/${examId}/subjects`" class="card text-decoration-none h-100">
            <div class="card-body d-flex align-items-center">
              <div class="rounded-circle bg-primary bg-opacity-10 p-3 me-3">
                <vue-feather type="book" size="22" class="text-primary" />
              </div>
              <div>
                <h6 class="mb-1">Subjects</h6>
                <small class="text-muted">Manage exam subjects</small>
              </div>
              <vue-feather type="chevron-right" size="18" class="ms-auto text-muted" />
            </div>
          </router-link>
        </div>
        <div class="col-md-4">
          <router-link :to="`/app/exams/${examId}/test-series`" class="card text-decoration-none h-100">
            <div class="card-body d-flex align-items-center">
              <div class="rounded-circle bg-info bg-opacity-10 p-3 me-3">
                <vue-feather type="layers" size="22" class="text-info" />
              </div>
              <div>
                <h6 class="mb-1">Test Series</h6>
                <small class="text-muted">Manage mock test series</small>
              </div>
              <vue-feather type="chevron-right" size="18" class="ms-auto text-muted" />
            </div>
          </router-link>
        </div>
        <div class="col-md-4">
          <router-link :to="`/app/exams/${examId}/questions`" class="card text-decoration-none h-100">
            <div class="card-body d-flex align-items-center">
              <div class="rounded-circle bg-warning bg-opacity-10 p-3 me-3">
                <vue-feather type="help-circle" size="22" class="text-warning" />
              </div>
              <div>
                <h6 class="mb-1">Questions</h6>
                <small class="text-muted">Manage exam questions</small>
              </div>
              <vue-feather type="chevron-right" size="18" class="ms-auto text-muted" />
            </div>
          </router-link>
        </div>
      </div>

      <div class="card">
        <div class="card-header">
          <h6 class="card-title mb-0">Edit Exam</h6>
        </div>
        <div class="card-body">
          <form @submit.prevent="handleSubmit">
            <div class="row">
              <div class="col-lg-8">
                <div class="row">
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Exam Name <span class="text-danger">*</span></label>
                    <input v-model="form.name" type="text" class="form-control" required />
                  </div>
                  <div class="col-md-6 mb-3">
                    <label class="form-label">Category <span class="text-danger">*</span></label>
                    <input v-model="form.category" type="text" class="form-control" placeholder="e.g., Government, Banking, Engineering" required />
                  </div>
                </div>

                <div class="mb-3">
                  <label class="form-label">Description</label>
                  <textarea v-model="form.description" class="form-control" rows="3"></textarea>
                </div>

                <div class="row">
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Status</label>
                    <select v-model="form.status" class="form-select">
                      <option value="draft">Draft</option>
                      <option value="active">Active</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                  <div class="col-md-4 mb-3">
                    <label class="form-label">Thumbnail URL</label>
                    <input v-model="form.thumbnail" type="url" class="form-control" placeholder="https://..." />
                  </div>
                </div>

                <div v-if="error" class="alert alert-danger">{{ error }}</div>

                <div class="d-flex gap-2 pt-2">
                  <button type="submit" class="btn btn-primary" :disabled="loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
                    Update Exam
                  </button>
                  <router-link to="/app/exams/list" class="btn btn-outline-secondary">Cancel</router-link>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import api from '@/services/api'
import { useAlert } from '@/plugins/sweetalert'

export default {
  name: 'EditExam',
  setup() {
    const route = useRoute()
    const router = useRouter()
    const alert = useAlert()
    const examId = route.params.id
    
    const breadcrumbs = [
      { title: 'Dashboard', url: '/app/dashboard' },
      { title: 'Exams', url: '/app/exams/list' },
      { title: 'Edit Exam', active: true }
    ]

    const form = ref({
      name: '',
      category: '',
      description: '',
      thumbnail: '',
      status: 'draft'
    })
    const loading = ref(false)
    const pageLoading = ref(true)
    const error = ref('')

    const fetchExam = async () => {
      pageLoading.value = true
      try {
        const response = await api.get(`/v1/admin/exams/${examId}`)
        const exam = response.data
        if (!exam) {
          throw new Error('Exam data not found in response')
        }
        form.value = {
          name: exam.name || '',
          category: exam.category || '',
          description: exam.description || '',
          thumbnail: exam.thumbnail || '',
          status: exam.status || 'draft'
        }
      } catch (err) {
        console.error('Failed to fetch exam:', err)
        const message = err.response?.status === 404 
          ? 'Exam not found' 
          : err.response?.data?.message || err.message || 'Failed to load exam'
        await alert.error('Error', message)
        router.push('/app/exams/list')
      } finally {
        pageLoading.value = false
      }
    }

    const handleSubmit = async () => {
      loading.value = true
      error.value = ''

      try {
        await api.put(`/v1/admin/exams/${examId}`, { exam: form.value })
        alert.success('Success', 'Exam updated successfully')
        router.push('/app/exams/list')
      } catch (err) {
        error.value = err.response?.data?.message || 'Failed to update exam'
      } finally {
        loading.value = false
      }
    }

    onMounted(fetchExam)

    return { examId, breadcrumbs, form, loading, pageLoading, error, handleSubmit }
  }
}
</script>
